import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const newRequestId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const todayStr = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(new Date());

export const formatCurrency = (amountInKurus) => {
  const digits = (amountInKurus || 0) % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: digits, maximumFractionDigits: digits }).format((amountInKurus || 0) / 100);
};

const fmt = (dateStr, opts) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return new Intl.DateTimeFormat('tr-TR', { timeZone: 'Europe/Istanbul', ...opts }).format(d);
};
export const formatDate = (s) => fmt(s, { day: '2-digit', month: 'short', year: 'numeric' });
export const formatDateTime = (s) => fmt(s, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const toKurus = (v) => Math.round(parseFloat(String(v).replace(',', '.')) * 100);

export function ReasonDialog({ open, title, actionLabel, onClose, onConfirm, submitting }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reason-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <Label>Gerekçe *</Label>
        <Textarea data-testid="reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Neden bu işlemi yapıyorsunuz?" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button data-testid="reason-confirm-btn" variant="destructive" disabled={submitting || !reason.trim()} onClick={() => onConfirm(reason.trim())}>
            {submitting ? 'İşleniyor...' : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyRenewal = () => ({ device_count: '', device_ids: [], term_years: '', renewal_amount: '', renewal_date: todayStr(), new_subscription_start: '', new_subscription_end: '', description: '', reason: '' });

function DevicePicker({ available, selected, onChange, priceOf }) {
  const toggle = (d) => onChange(selected.includes(d) ? selected.filter(x => x !== d) : [...selected, d]);
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <Label>Yenilenen Cihazlar * ({selected.length} seçili)</Label>
        <button type="button" data-testid="renewal-select-all-btn" className="text-xs text-blue-600 hover:underline"
          onClick={() => onChange(selected.length === available.length ? [] : [...available])}>
          {selected.length === available.length ? 'Seçimi kaldır' : 'Tümünü seç'}
        </button>
      </div>
      <div className="border rounded-lg max-h-48 overflow-y-auto divide-y" data-testid="renewal-device-list">
        {available.map(d => (
          <label key={d} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
            <Checkbox className="h-4 w-4 shrink-0" data-testid={`renewal-device-${d}`} checked={selected.includes(d)} onCheckedChange={() => toggle(d)} />
            <span className="flex-1">{d}</span>
            {priceOf(d) > 0 && <span className="text-xs text-gray-500">{formatCurrency(priceOf(d))}</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

export const TERMS = ['1', '2', '3'];

const addYears = (start, years) => {
  const d = new Date(`${start}T00:00:00`);
  if (isNaN(d)) return '';
  d.setFullYear(d.getFullYear() + years);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const devicePrice = (caseData, device, term) => caseData.device_prices?.[device]?.[term] || caseData.unit_prices?.[term] || 0;

export const termTotal = (caseData, term, devices = caseData.devices || []) =>
  devices.length
    ? devices.reduce((s, d) => s + devicePrice(caseData, d, term), 0)
    : (caseData.unit_prices?.[term] || 0) * caseData.source_device_count;

export function RenewalDialog({ open, onClose, onSubmit, submitting, caseData, editing }) {
  const [form, setForm] = useState(emptyRenewal());
  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      device_count: String(editing.device_count), device_ids: editing.device_ids || [],
      term_years: editing.term_years ? String(editing.term_years) : '',
      renewal_amount: String(editing.renewal_amount / 100), renewal_date: editing.renewal_date,
      new_subscription_start: editing.new_subscription_start, new_subscription_end: editing.new_subscription_end,
      description: editing.description || '', reason: ''
    } : emptyRenewal());
  }, [open, editing]);

  const active = (caseData.renewals || []).filter(r => !r.is_cancelled && r.id !== editing?.id);
  const usedDevices = new Set(active.flatMap(r => r.device_ids || []));
  const hasList = (caseData.devices || []).length > 0;
  const available = (caseData.devices || []).filter(d => !usedDevices.has(d));
  const remaining = caseData.source_device_count - active.reduce((s, r) => s + r.device_count, 0);
  const count = hasList ? form.device_ids.length : parseInt(form.device_count || '0', 10);
  const willBe = count >= remaining ? 'Tam yenilendi' : 'Kısmi yenilendi';
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const listPrice = (f) => {
    if (!f.term_years) return 0;
    if (hasList) return f.device_ids.reduce((s, d) => s + devicePrice(caseData, d, f.term_years), 0);
    return (caseData.unit_prices?.[f.term_years] || 0) * parseInt(f.device_count || '0', 10);
  };
  const withAuto = (next) => {
    const price = listPrice(next);
    const auto = { ...next };
    if (price > 0) auto.renewal_amount = String(price / 100);
    if (next.term_years && next.new_subscription_start) auto.new_subscription_end = addYears(next.new_subscription_start, parseInt(next.term_years, 10));
    return auto;
  };
  const setAuto = (k) => (e) => setForm(withAuto({ ...form, [k]: e.target.value }));
  const currentList = listPrice(form);

  const submit = () => {
    if (!form.term_years) return onSubmit(null, 'Yenileme süresini seçiniz (1, 2 veya 3 yıl)');
    if (count <= 0) return onSubmit(null, 'Yenilenen cihaz seçiniz veya adet giriniz');
    if (count > remaining) return onSubmit(null, `En fazla ${remaining} cihaz yenilenebilir`);
    if (!form.renewal_amount || toKurus(form.renewal_amount) <= 0) return onSubmit(null, 'Yenileme tutarı giriniz');
    if (!form.new_subscription_start || !form.new_subscription_end) return onSubmit(null, 'Yeni abonelik tarihlerini giriniz');
    if (form.new_subscription_end <= form.new_subscription_start) return onSubmit(null, 'Bitiş tarihi başlangıçtan sonra olmalıdır');
    if (editing && !form.reason.trim()) return onSubmit(null, 'Düzeltme gerekçesi zorunludur');
    onSubmit({
      device_count: count, device_ids: hasList ? form.device_ids : [], term_years: parseInt(form.term_years, 10),
      renewal_amount: toKurus(form.renewal_amount), renewal_date: form.renewal_date,
      new_subscription_start: form.new_subscription_start, new_subscription_end: form.new_subscription_end,
      description: form.description, ...(editing ? { reason: form.reason.trim() } : {})
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="renewal-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Yenilemeyi Düzelt' : 'Yenileme Ekle'}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700" data-testid="renewal-remaining-info">
          Yenilenebilir cihaz: <b>{remaining}</b> / {caseData.source_device_count}
          {count > 0 && count <= remaining && <span className="block mt-1">Bu kayıtla dosya: <b data-testid="renewal-will-be">{willBe}</b></span>}
        </div>
        <div className="space-y-4">
          <div>
            <Label>Yenileme Süresi *</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {TERMS.map(t => (
                <Button key={t} type="button" data-testid={`renewal-term-${t}`} variant={form.term_years === t ? 'default' : 'outline'}
                  onClick={() => setForm(withAuto({ ...form, term_years: t }))} className="flex flex-col h-auto py-2">
                  <span>{t} Yıl</span>
                  {listPrice({ ...form, term_years: t }) > 0 && <span className="text-[10px] opacity-70" data-testid={`renewal-term-${t}-price`}>{formatCurrency(listPrice({ ...form, term_years: t }))}</span>}
                </Button>
              ))}
            </div>
          </div>
          {hasList ? (
            <DevicePicker available={available} selected={form.device_ids} priceOf={(d) => devicePrice(caseData, d, form.term_years)}
              onChange={(ids) => setForm(withAuto({ ...form, device_ids: ids }))} />
          ) : (
            <div>
              <Label>Yenilenen Cihaz Adedi *</Label>
              <Input data-testid="renewal-device-count-input" type="number" min="1" max={remaining} value={form.device_count} onChange={setAuto('device_count')} placeholder="0" />
            </div>
          )}
          <div>
            <Label>Kesinleşen Yenileme Tutarı (TL, KDV dahil) *</Label>
            <Input data-testid="renewal-amount-input" type="number" min="0" step="0.01" value={form.renewal_amount} onChange={set('renewal_amount')} placeholder="0" />
            {currentList > 0 && <p className="text-xs text-gray-500 mt-1" data-testid="renewal-list-price">Liste fiyatı: {formatCurrency(currentList)} (değiştirebilirsiniz)</p>}
          </div>
          <div>
            <Label>Yenileme Tarihi</Label>
            <Input data-testid="renewal-date-input" type="date" value={form.renewal_date} onChange={set('renewal_date')} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Başlangıç *</Label>
              <Input data-testid="renewal-start-input" type="date" value={form.new_subscription_start} onChange={setAuto('new_subscription_start')} />
            </div>
            <div>
              <Label>Bitiş *</Label>
              <Input data-testid="renewal-end-input" type="date" value={form.new_subscription_end} onChange={set('new_subscription_end')} />
            </div>
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea data-testid="renewal-description-input" value={form.description} onChange={set('description')} rows={2} />
          </div>
          {editing && (
            <div>
              <Label>Düzeltme Gerekçesi *</Label>
              <Textarea data-testid="renewal-reason-input" value={form.reason} onChange={set('reason')} rows={2} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button data-testid="renewal-submit-btn" onClick={submit} disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Kaydet'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyPayment = () => ({ payment_amount: '', payment_date: todayStr(), payment_method: '', reference: '', description: '', reason: '' });

export function PaymentDialog({ open, onClose, onSubmit, submitting, caseData, editing }) {
  const [form, setForm] = useState(emptyPayment());
  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      payment_amount: String(editing.payment_amount / 100), payment_date: editing.payment_date,
      payment_method: editing.payment_method, reference: editing.reference || '', description: editing.description || '', reason: ''
    } : emptyPayment());
  }, [open, editing]);

  const maxKurus = caseData.remaining_balance + (editing ? editing.payment_amount : 0);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = () => {
    const amount = toKurus(form.payment_amount);
    if (!form.payment_amount || amount <= 0) return onSubmit(null, 'Tahsilat tutarı giriniz');
    if (amount > maxKurus) return onSubmit(null, `Tahsilat kalan bakiyeyi (${formatCurrency(maxKurus)}) aşamaz`);
    if (!form.payment_method) return onSubmit(null, 'Ödeme yöntemi seçiniz');
    if (editing && !form.reason.trim()) return onSubmit(null, 'Düzeltme gerekçesi zorunludur');
    onSubmit({
      payment_amount: amount, payment_date: form.payment_date, payment_method: form.payment_method,
      reference: form.reference, description: form.description, ...(editing ? { reason: form.reason.trim() } : {})
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Tahsilatı Düzelt' : 'Tahsilat Ekle'}</DialogTitle></DialogHeader>
        <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-600">
          Tahsil edilebilir en fazla: <b className="text-amber-600" data-testid="payment-max-amount">{formatCurrency(maxKurus)}</b>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Tahsilat Tutarı (TL) *</Label>
            <Input data-testid="payment-amount-input" type="number" min="0" step="0.01" max={maxKurus / 100} value={form.payment_amount} onChange={set('payment_amount')} placeholder="0" />
            <button type="button" data-testid="payment-fill-max-btn" className="text-xs text-blue-600 hover:underline mt-1" onClick={() => setForm({ ...form, payment_amount: String(maxKurus / 100) })}>
              Kalanın tamamını gir
            </button>
          </div>
          <div>
            <Label>Ödeme Yöntemi *</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent>
                {['Havale/EFT', 'Kredi kartı', 'Nakit', 'Diğer'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tahsilat Tarihi</Label>
            <Input data-testid="payment-date-input" type="date" value={form.payment_date} onChange={set('payment_date')} />
          </div>
          <div>
            <Label>Dekont/İşlem Referansı</Label>
            <Input data-testid="payment-reference-input" value={form.reference} onChange={set('reference')} />
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea data-testid="payment-description-input" value={form.description} onChange={set('description')} rows={2} />
          </div>
          {editing && (
            <div>
              <Label>Düzeltme Gerekçesi *</Label>
              <Textarea data-testid="payment-reason-input" value={form.reason} onChange={set('reason')} rows={2} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button data-testid="payment-submit-btn" onClick={submit} disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Kaydet'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
